import React, { useState } from 'react';

import {
  CancelSurveyModal,
  CancelSurveyFeedback,
  useSciDomain,
} from 'components';
import { CountdownTimer } from 'components/CountdownTimer';
import { EcomCardForm } from 'components/EcomCardForm';
import { EcomOtpForm } from 'components/EcomOtpForm';
import { EcomRedirect as DefaultEcomRedirect } from 'components/EcomRedirect';
import { ProviderPaymentRedirect as DefaultProviderPaymentRedirect } from 'components/ProviderPaymentRedirect';
import { CustomerConfirmStatusDetails, OrderStatus, SciTemplate } from 'enums';
import {
  useCancelSurveyReasons,
  useGatewayRedirect,
  useOrderContext,
} from 'hooks';
import {
  OrderLayoutProps,
  PaymentCardViewProps,
  PaymentDetailsViewProps,
  PaymentLoadingViewProps,
  PaymentRedirectViewProps,
  PaymentRulesViewProps,
  PaymentStatusViewProps,
} from 'types';
import {
  DefaultCustomerPaidReceipts,
  DefaultLayout,
  DefaultPaymentCard,
  DefaultPaymentDetails,
  DefaultPaymentLoading,
  DefaultPaymentRulesView,
  DefaultPaymentStatus,
} from 'views/DefaultOrderView/components';
import { SqAlternativeOrderAmountSelectionModal } from 'views/SqOrderView';

type Props = {
  overrideComponents?: {
    Layout?: React.FC<OrderLayoutProps>;
    PaymentStatus?: React.FC<PaymentStatusViewProps>;
    PaymentDetails?: React.FC<PaymentDetailsViewProps>;
    PaymentRules?: React.FC<PaymentRulesViewProps>;
    PaymentLoading?: React.FC<PaymentLoadingViewProps>;
    CustomerPaidReceipts?: React.FC;
    PaymentCard?: React.FC<PaymentCardViewProps>;
    EcomRedirect?: React.FC<PaymentRedirectViewProps>;
    ProviderPaymentRedirect?: React.FC<PaymentRedirectViewProps>;
  };
};

export const BaseOrderView: React.FC<Props> = ({
  overrideComponents: {
    Layout = DefaultLayout,
    PaymentStatus = DefaultPaymentStatus,
    PaymentDetails = DefaultPaymentDetails,
    PaymentRules = DefaultPaymentRulesView,
    PaymentLoading = DefaultPaymentLoading,
    CustomerPaidReceipts = DefaultCustomerPaidReceipts,
    PaymentCard = DefaultPaymentCard,
    EcomRedirect = DefaultEcomRedirect,
    ProviderPaymentRedirect = DefaultProviderPaymentRedirect,
  } = {},
}) => {
  const { data: sciDomainConfig } = useSciDomain();
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);

  const {
    order,
    refetchOrderStatus,
    tradeMethodSelection: {
      formattedTradeMethods,
      selectedTradeMethod,
      startPaymentSyncFailed,
    },
    paymentForm: {
      customerFields,
      isCustomerDataConfirmed,
      customerDataBeforePayment,
      paymentFieldsLoading,
    },
    receiptsResult: { canManageReceipts },
    orderActions: {
      actions,
      selectPaymentType: onSelectPaymentType,
      submitEcomCard,
      submitEcomConfirmCode,
      isEcomSubmitInProgress,
      isCancelLoading,
      cancelWithFeedback,
    },
    paymentTypeFlags: { isEcom },
    paymentFormValues,
    status,
    isTSBPSingleBankSelection,
    isStartPaymentSyncInProgress,
    isStartRequisitesCascadeLoading,
    formattedPaymentFields,
    shouldShowQrCode,
    onPaymentFormValuesChange,
    currencySymbol,
    showAlternativeSumModal,
    onSelectAlternativeAmount,
    onSelectAlternativeTradeMethod,
    onDismissAlternativeSumModal,
  } = useOrderContext();

  const isSqTemplate = sciDomainConfig?.template === SciTemplate.SQ;
  const { reasons: cancelSurveyReasons } = useCancelSurveyReasons();
  const { ecomRedirectLink, providerRedirectLink, redirectLink } =
    useGatewayRedirect();

  const effectiveCancelAction = isSqTemplate
    ? actions.cancel
    : { ...actions.cancel, onClick: () => setIsSurveyOpen(true) };

  const handleSurveyConfirm = (feedback: CancelSurveyFeedback) => {
    setIsSurveyOpen(false);
    const hasFeedback =
      feedback.reasons.length > 0 || feedback.comment.trim().length > 0;
    cancelWithFeedback(hasFeedback ? feedback : undefined);
  };

  const handleSurveyDismiss = () => setIsSurveyOpen(false);

  const canShowPaymentDetails =
    !!selectedTradeMethod ||
    formattedPaymentFields.length > 0 ||
    shouldShowQrCode;

  const shouldShowPaymentCard =
    !!selectedTradeMethod &&
    !isCustomerDataConfirmed &&
    (customerDataBeforePayment ||
      order?.statusDetails === CustomerConfirmStatusDetails.CustomerPayed);

  // Keep the ecom card form mounted while the payment sync is in flight (and out of the PaymentLoading
  // swap below). Unmounting it on submit dropped the customer's typed card, so a rejected/failed sync
  // came back to a blank form.
  const isEcomCardEntry =
    status === OrderStatus.New && isEcom && !startPaymentSyncFailed;

  // Countdown for the ecom entry/OTP screens; the order carries statusTimeoutAt for New (30m) and
  // WaitingConfirmation/CustomerConfirm (payment timeout), matching the platform-side timeout crons.
  const ecomTimer = order?.statusTimeoutAt ? (
    <div className="tw-mb-4 tw-flex tw-justify-center">
      <CountdownTimer order={order} refetchOrderStatus={refetchOrderStatus} />
    </div>
  ) : null;

  return (
    <Layout>
      {status === OrderStatus.New &&
        !isStartRequisitesCascadeLoading &&
        (startPaymentSyncFailed ? (
          <PaymentStatus actions={[actions.selectTradeMethod]} />
        ) : isEcom ? (
          <>
            {ecomTimer}
            <EcomCardForm
              onSubmit={submitEcomCard}
              onCancel={() => effectiveCancelAction.onClick?.()}
              isLoading={isEcomSubmitInProgress || isStartPaymentSyncInProgress}
            />
          </>
        ) : (
          !isStartPaymentSyncInProgress && (
            <PaymentRules
              tradeMethods={formattedTradeMethods}
              selectedTradeMethod={selectedTradeMethod}
              handleSelect={onSelectPaymentType}
            />
          )
        ))}
      {ecomRedirectLink && <EcomRedirect paymentLink={ecomRedirectLink} />}
      {providerRedirectLink && (
        <ProviderPaymentRedirect paymentLink={providerRedirectLink} />
      )}
      {isEcom && status === OrderStatus.WaitingConfirmation && (
        <>
          {ecomTimer}
          <EcomOtpForm
            onSubmit={submitEcomConfirmCode}
            onCancel={() => effectiveCancelAction.onClick?.()}
            isLoading={isEcomSubmitInProgress}
          />
        </>
      )}
      {!redirectLink &&
        (status === OrderStatus.Requisites ||
          paymentFieldsLoading ||
          (isStartPaymentSyncInProgress && !isEcomCardEntry) ||
          isStartRequisitesCascadeLoading) && (
          <>
            <PaymentLoading status={OrderStatus.Requisites} />
            <SqAlternativeOrderAmountSelectionModal
              open={showAlternativeSumModal}
              orderId={order?.id ?? ''}
              currency={currencySymbol}
              alternativeAmountEnabled={order?.shop?.alternativeAmountEnabled}
              alternativeTradeMethodEnabled={
                order?.shop?.alternativeTradeMethodEnabled
              }
              onSelect={onSelectAlternativeAmount}
              onSelectTradeMethod={onSelectAlternativeTradeMethod}
              onDismiss={onDismissAlternativeSumModal}
            />
          </>
        )}
      {status === OrderStatus.CustomerConfirm &&
        !ecomRedirectLink &&
        !providerRedirectLink &&
        !paymentFieldsLoading &&
        canShowPaymentDetails &&
        (shouldShowPaymentCard ? (
          <PaymentCard
            tradeMethod={selectedTradeMethod}
            customerFields={customerFields}
            paymentFormValues={paymentFormValues}
            onPaymentFormValuesChange={onPaymentFormValuesChange}
            refetchOrderStatus={refetchOrderStatus}
            actions={
              isTSBPSingleBankSelection
                ? [effectiveCancelAction]
                : [effectiveCancelAction, actions.confirmCard]
            }
          />
        ) : order?.statusDetails !==
          CustomerConfirmStatusDetails.CustomerPayed ? (
          <PaymentDetails
            actions={[effectiveCancelAction, actions.confirmPayment]}
          />
        ) : (
          order?.shop?.collectCustomerReceipts && <CustomerPaidReceipts />
        ))}
      {[OrderStatus.TraderConfirm, OrderStatus.HoldCompleted].includes(
        status,
      ) && <PaymentLoading status={status} />}
      {(status === OrderStatus.Completed ||
        status === OrderStatus.Cancelled ||
        status === OrderStatus.Dispute) && (
        <PaymentStatus
          actions={[actions.backToShop]}
          canManageReceipts={canManageReceipts}
        />
      )}

      {!isSqTemplate && (
        <CancelSurveyModal
          open={isSurveyOpen}
          reasons={cancelSurveyReasons}
          onConfirm={handleSurveyConfirm}
          onDismiss={handleSurveyDismiss}
          isLoading={isCancelLoading}
        />
      )}
    </Layout>
  );
};
